/**
 * Native bootstrap — wires Capacitor plugins to the existing app.
 *
 * Everything here is guarded by `isNative`, and the plugins are imported
 * dynamically so the web bundle never pulls native plugin code. Called once
 * from <NativeProvider/> (App.jsx) after the app mounts.
 *
 * Responsibilities:
 *   - Status bar: style + colour tracked to the in-app `data-theme`.
 *   - Splash screen: hidden after first paint.
 *   - Keyboard: toggle a `.keyboard-open` body class so fixed chrome
 *     (bottom-nav / FAB) can get out of the way.
 *   - Hardware back button: close an open overlay → go back → minimize.
 */
import { isNative, isAndroid } from '../utils/platform';
import { closeTopOverlay } from './backStack';
import { registerPush } from './push';

const ROOT_TABS = new Set([
  '/', '/dashboard', '/teacher/dashboard', '/worker/dashboard',
  '/students', '/patients', '/lessons', '/calendar',
]);

function currentPath() {
  // HashRouter on native → real path lives in the hash (#/dashboard).
  const hash = window.location.hash || '';
  if (hash.startsWith('#/')) return hash.slice(1).split('?')[0];
  return window.location.pathname;
}

async function applyStatusBarTheme(StatusBar, Style) {
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  try {
    // Dark UI → light status-bar content, and vice-versa.
    await StatusBar.setStyle({ style: dark ? Style.Light : Style.Dark });
    if (isAndroid) {
      await StatusBar.setBackgroundColor({ color: dark ? '#0F172A' : '#355760' });
    }
  } catch { /* status bar not available */ }
}

export async function initNative() {
  if (!isNative) return;

  // ---- Status bar (theme-coordinated) -------------------------------------
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await applyStatusBarTheme(StatusBar, Style);
    // Re-apply whenever the app toggles data-theme (light/dark/system).
    const obs = new MutationObserver(() => applyStatusBarTheme(StatusBar, Style));
    obs.observe(document.documentElement, {
      attributes: true, attributeFilter: ['data-theme'],
    });
  } catch { /* plugin missing */ }

  // ---- Splash screen ------------------------------------------------------
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch { /* plugin missing */ }

  // ---- Keyboard -----------------------------------------------------------
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-open');
    });
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-open');
    });
  } catch { /* plugin missing */ }

  // ---- Hardware back button -----------------------------------------------
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', ({ canGoBack }) => {
      // 1) An open overlay swallows the back press.
      if (closeTopOverlay()) return;
      // 2) Not at a root tab and history exists → go back.
      if (canGoBack && !ROOT_TABS.has(currentPath())) {
        window.history.back();
        return;
      }
      // 3) At a root tab → background the app rather than abruptly killing it.
      App.minimizeApp?.();
    });
  } catch { /* plugin missing */ }

  // ---- Push notifications -------------------------------------------------
  // No-ops gracefully until FCM/APNs credentials are configured.
  registerPush();
}
